import React, { useRef, useEffect } from 'react';
import { styled, css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import IntroductionNew from '@atlassian/jira-illustrations/src/ui/adg4/jira/heroes/assets/graph-go-up-with-people.svg';
import UFOLoadHold from '@atlassian/ufo-load-hold';
import { useOnGadgetRenderEffect } from '../../../../controllers/above-the-fold';
import { useLinkInterceptor } from '../../../../controllers/link-interceptor';
import Introduction from './assets/introduction.png';

export type InlineHtmlProps = {
	isLoading: boolean;
	id: string;
	inlineHtml: string;
	setIsLoading: (arg1: boolean) => void;
};

export const InlineHtml = ({ id, inlineHtml, isLoading, setIsLoading }: InlineHtmlProps) => {
	const inlineHtmlRef = useRef<HTMLDivElement | null>(null);
	useLinkInterceptor(inlineHtmlRef.current, isLoading);

	useEffect(() => {
		const slotHtml = document.createRange().createContextualFragment(inlineHtml);
		if (inlineHtmlRef.current) {
			inlineHtmlRef.current.innerHTML = '';
			inlineHtmlRef.current.appendChild(slotHtml);
		}
		setIsLoading(false);
	}, [inlineHtml, setIsLoading]);

	useOnGadgetRenderEffect(id, inlineHtmlRef.current);

	return (
		<>
			<UFOLoadHold name="inline-html-gadget" hold={isLoading} />
			<Container>
				<div id={`gadget-${id}`} ref={inlineHtmlRef} />
			</Container>
		</>
	);
};

// from com.atlassian.jira.jira-atlaskit-plugin:overrides-dashboard
const styleOverrideFromMonolithStylesOld = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.g-intro': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.intro': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-cols': {
				marginTop: token('space.200', '16px'),
				display: 'flex',
				justifyContent: 'space-around',
				height: '130px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-logo': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				backgroundImage: `url(${Introduction})`,
				backgroundPosition: 'left top',
				backgroundRepeat: 'no-repeat',
				backgroundSize: '100%',
				width: '20%',
				minWidth: '120px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-message': {
				marginLeft: token('space.200', '16px'),
			},
		},
		padding: `0 ${token('space.200', '16px')}`,
	},
});

// from com.atlassian.jira.jira-atlaskit-plugin:overrides-dashboard
const styleOverrideFromMonolithStylesNew = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.g-intro': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.intro': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-cols': {
				marginTop: token('space.200', '16px'),
				display: 'flex',
				justifyContent: 'space-around',
				height: '130px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-logo': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				backgroundImage: `url(${IntroductionNew})`,
				backgroundPosition: 'left top',
				backgroundRepeat: 'no-repeat',
				backgroundSize: 'contain',
				width: '20%',
				minWidth: '120px',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.intro-message': {
				marginLeft: token('space.200', '16px'),
			},
		},
		padding: `0 ${token('space.200', '16px')}`,
	},
});

const Container = componentWithFF(
	'endeavour.dashboard.enghealth-7090-consolidate-jfe-assets',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div(
		{
			position: 'relative',
			overflow: 'auto',
		},
		styleOverrideFromMonolithStylesNew,
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div(
		{
			position: 'relative',
			overflow: 'auto',
		},
		styleOverrideFromMonolithStylesOld,
	),
);
